import { avSelectors } from "@minervaproject/avsubsystem";
import clsx from "clsx";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";

import { MoreParticipants } from "./more_participants";
import { Participant } from "./participant";

import { FlightErrorBoundary } from "components/error_boundary";
import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { useUsersProperties } from "hooks/use_user_properties";
import { CapabilityType, SizeType } from "shared/types";
import { UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";

const MAX_PARTICIPANT_HEIGHT = 160;
const PARTICIPANT_BOTTOM_MARGIN = 4;
export const PARTICIPANT_BAR_Z_INDEX = 301; // above tldraw zoom component, but below tldraw palette component

function Participants({
  children,
  className,
  isVertical = false,
}: {
  children: ReactNode;
  className?: string;
  isVertical?: boolean;
}) {
  return (
    <div
      className={clsx("flex flex-auto pb1 pl1 bg-black", className, {
        "flex-column p1": isVertical,
        "pb1 pl1": !isVertical,
      })}
    >
      {children}
    </div>
  );
}

function getOrderForRole(user: UserType) {
  // Guest instructors are ordered first, then teachers, then everyone else.
  const roleToOrder: { [key: string]: number } = {
    "guest-instructor": 1,
    teacher: 2,
  };
  return roleToOrder[user.role] || user.sort_order;
}

function ParticipantBar({ isVertical }: { isVertical?: boolean }) {
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const users = Object.values(useUsersProperties(["is_present", "role"]));
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const canControlClassroomUserIds = useUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const userIdsWithVideoEnabledAndAvailable =
    avSelectors.useUserIdsWithStreamsWithVideoEnabledAndAvailable() as string[];
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());
  const isMobile = useIsMobile();
  const widthAdjustment = useSidebarAdjustment();
  let filteredUsers;
  const containerRef = useRef<HTMLDivElement>(null);
  const [participantHeight, setParticipantHeight] = useState(0);
  const [profilePhotoSize, setProfilePhotoSize] = useState<SizeType>("lg");
  const [signalSize, setSignalSize] = useState<SizeType>("md");

  // participant_bar_user_ids is only used in the main classroom, while breakout rooms look
  // at assigned_user_ids and visiting_user_ids to determine who is in the participant bar.
  if (breakoutRoom) {
    filteredUsers = users.filter(
      ({ id, is_present }) =>
        is_present &&
        isVisibleInClassUserIds.includes(id) &&
        (breakoutRoom.assigned_user_ids.includes(id) ||
          breakoutRoom.visiting_user_ids.includes(id)),
    );
  } else {
    // For in-person classes, we only show starred users in the participants bar.
    // For in-person classes, we also hide (but do not filter out) all starred users whose video is disabled.
    // This is because the underlying `Participant` component needs to be loaded to handle the AV if the
    // instructor re-enables their video later.
    filteredUsers = users.filter(({ id }) => participantBarUserIds.includes(id));
  }

  const sortedFilteredUsers = filteredUsers.sort((a, b) => {
    if (canControlClassroomUserIds.includes(a.id) && !canControlClassroomUserIds.includes(b.id)) {
      return -1;
    }
    if (canControlClassroomUserIds.includes(b.id) && !canControlClassroomUserIds.includes(a.id)) {
      return 1;
    }
    return getOrderForRole(a) - getOrderForRole(b);
  });

  const shouldHideParticipants =
    isInPerson && (isMobile || userIdsWithVideoEnabledAndAvailable.length === 0);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        const containerHeight = entries[0].contentRect.height;
        const _participantHeight = isVertical
          ? sortedFilteredUsers.length > 0
            ? (containerHeight - sortedFilteredUsers.length * PARTICIPANT_BOTTOM_MARGIN) /
              sortedFilteredUsers.length
            : 0
          : containerHeight;
        setParticipantHeight(Math.min(_participantHeight, MAX_PARTICIPANT_HEIGHT)); // only needed for vertical PBs (i.e., in breakout room)

        if (_participantHeight < 80) {
          setProfilePhotoSize("md");
          setSignalSize("sm");
        } else if (_participantHeight > 120) {
          setProfilePhotoSize("xl");
          setSignalSize("lg");
        } else {
          setProfilePhotoSize("lg");
          setSignalSize("md");
        }
      });
    });

    let containerElement: Element | null | undefined;

    const getElement = () => {
      containerElement = containerRef?.current;
      if (containerElement) {
        resizeObserver.observe(containerElement);
      } else {
        setTimeout(getElement, 100);
      }
    };
    getElement();

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, [isVertical, sortedFilteredUsers.length]);

  return (
    <div
      className="flex justify-between"
      style={
        !isVertical
          ? {
              width: widthAdjustment,
              zIndex: PARTICIPANT_BAR_Z_INDEX,
            }
          : {
              zIndex: PARTICIPANT_BAR_Z_INDEX,
            }
      }
      data-testid="participant-bar"
      ref={containerRef}
    >
      <Participants
        isVertical={isVertical}
        className={clsx({ "display-none": shouldHideParticipants })}
      >
        {sortedFilteredUsers.map((user, index) => (
          <FlightErrorBoundary
            key={user.id}
            text="This participant tile cannot be displayed"
            margin="0 8px 8px"
            width="200px"
          >
            <Participant
              userId={user.id}
              canControlClassroom={canControlClassroomUserIds.includes(user.id)}
              currentUserCanControlClassroom={canControlClassroomUserIds.includes(currentUserId)}
              isSelf={user.id === currentUserId}
              profilePhotoSize={profilePhotoSize}
              signalSize={signalSize}
              isSquircle={isInPerson}
              index={index}
              participantCount={sortedFilteredUsers.length}
              className={clsx({
                "display-none":
                  isInPerson && !userIdsWithVideoEnabledAndAvailable.includes(user.id),
              })}
              isVertical={isVertical}
              height={participantHeight}
            />
          </FlightErrorBoundary>
        ))}
      </Participants>
      {!isInPerson && !breakoutRoom && (
        <MoreParticipants
          users={users}
          participantBarUserIds={participantBarUserIds}
          isVisibleInClassUserIds={isVisibleInClassUserIds}
        />
      )}
    </div>
  );
}

export { ParticipantBar, Participants };
